import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import React, { useEffect } from 'react';
import { AvatarDropdown, AvatarName } from '@/components';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import { history } from '@umijs/max';
import ForbiddenPage from '@/pages/403';
import '@ant-design/v5-patch-for-react-19';

const isDev = process.env.NODE_ENV === 'development';

const API_BASE_URL =
  typeof REACT_APP_ENV !== 'undefined' && REACT_APP_ENV === 'production'
    ? 'https://bps.aigcempower.com'
    : 'https://bps.test.aigcempower.com';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  const performOidcExchange = async () => {
    if (typeof window === 'undefined') return;

    try {
      const url = new URL(window.location.href);
      const code = url.searchParams.get('code');
      if (!code) return;

      // 获取当前路径，用于 SSO 回调验证
      const redirectUri = `${API_BASE_URL}/welcome`;

      const res = await fetch(
        'https://acz.aigcempower.com/api/v1/auth/oidc/token',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            code,
            client_id: '0692f27c-958d-751f-8000-2bbabede441c',
            client_secret: '994a588b1ea6170963cd23a10afe349a',
            redirect_uri: redirectUri,
            callback_url: `${API_BASE_URL}/api/v1/base/callback/common`,
          }),
          credentials: 'include',
        },
      );

      if (!res.ok) return;
      const raw = await res.json().catch(() => null);
      if (!raw || typeof raw !== 'object') return;

      const data = (raw as any).data || (raw as any);

      const accessToken = data.access_token as string | undefined;
      const refreshToken = data.refresh_token as string | undefined;

      if (accessToken) {
        localStorage.setItem('token', accessToken);
      }

      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }

      // token 获取成功后，立刻调用用户资料接口，获取完整用户信息并缓存
      if (accessToken) {
        try {
          const profileRes = await fetch(
            `${API_BASE_URL}/api/v1/auth/user/profile`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              credentials: 'include',
            },
          );

          if (profileRes.ok) {
            const profileRaw = await profileRes.json().catch(() => null);
            const profileData =
              (profileRaw &&
                typeof profileRaw === 'object' &&
                (profileRaw as any).data) ||
              profileRaw;
            if (profileData && typeof profileData === 'object') {
              localStorage.setItem('user', JSON.stringify(profileData));
            }
          }
        } catch {
          // 获取 profile 失败时忽略，后续接口的 401 处理会兜底
        }
      }

      // 清除 URL 中的 code 参数，保持当前路径
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('code');
      const cleanUrl = newUrl.pathname + (newUrl.search || '');
      window.history.replaceState({}, '', cleanUrl);
    } catch {
    }
  };

  await performOidcExchange();

  const fetchUserInfo = async () => {
    // 如果 performOidcExchange 已经拿到并缓存了用户信息，优先直接返回，避免重复请求
    if (typeof window !== 'undefined') {
      const cachedUser = localStorage.getItem('user');
      if (cachedUser) {
        try {
          return JSON.parse(cachedUser) as API.CurrentUser;
        } catch {
          // 缓存解析失败则继续走远程请求
        }
      }
    }

    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      // 进入欢迎页时，如果连 refresh_token 都没有，直接走第三方登录
      if (typeof window !== 'undefined') {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          const redirectUri = `${API_BASE_URL}/welcome`;
          window.location.href =
            `https://acz.aigcempower.com/login?redirect_uri=${redirectUri}`;
          return undefined;
        }
      }

      // 有 refresh_token 的情况，不在这里做跳转，交给后续接口的 401 统一处理
      return undefined;
    }
  };
  return {
    fetchUserInfo: async () => {
      const currentUser = await fetchUserInfo();
      return currentUser;
    },
    currentUser: await fetchUserInfo(),
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [],
    avatarProps: {
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    // 自定义 403 页面
    unAccessible: <ForbiddenPage />,
    // 不再在运行时强制跳转到本地登录页，所有登录态问题统一交给全局 401 / 外部 SSO 处理
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],

    menuHeaderRender: undefined,
    childrenRender: (children) => {
      if (initialState?.currentUser && 
          initialState.currentUser.role !== 'admin' && 
          !initialState.currentUser.is_superuser) {
        const RedirectTo403 = () => {
          useEffect(() => {
            if (history.location.pathname !== '/403') {
              history.replace('/403');
            }
          }, []);
          return null;
        };
        return <RedirectTo403 />;
      }

      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // API 基础地址
  // 测试环境: https://bps.test.aigcempower.com
  // 生产环境: https://bps.aigcempower.com
  baseURL: API_BASE_URL,
  ...errorConfig,
};
